import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SuspensionReason } from '@/api/User/users.api';

interface User {
  _id: string;
  firstname?: string;
  lastname?: string;
  username: string;
  email?: string;
}

interface SuspendSellerModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onSuspend: (data: {
    userId: string;
    reason: SuspensionReason;
    reasonDetails?: string;
    expiresAt?: string;
    adminNotes?: string;
  }) => void;
  isPending?: boolean;
}

const SUSPENSION_REASONS: { value: SuspensionReason; label: string }[] = [
  { value: 'fraudulent_listings', label: 'Fraudulent Listings' },
  { value: 'policy_violations', label: 'Policy Violations' },
  { value: 'spam', label: 'Spam or Excessive Posting' },
  { value: 'scam', label: 'Scam Attempts' },
  { value: 'counterfeit_goods', label: 'Counterfeit Goods' },
  { value: 'harassment', label: 'Harassment of Other Users' },
  { value: 'multiple_reports', label: 'Multiple User Reports' },
  { value: 'other', label: 'Other' },
];

export function SuspendSellerModal({
  isOpen,
  onClose,
  user,
  onSuspend,
  isPending = false,
}: SuspendSellerModalProps) {
  const [reason, setReason] = useState<SuspensionReason | ''>('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [suspensionType, setSuspensionType] = useState<'permanent' | 'temporary'>('permanent');

  const resetForm = () => {
    setReason('');
    setReasonDetails('');
    setExpiresAt('');
    setAdminNotes('');
    setSuspensionType('permanent');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    if (!user || !reason) return;

    onSuspend({
      userId: user._id,
      reason,
      reasonDetails: reasonDetails || undefined,
      expiresAt: suspensionType === 'temporary' && expiresAt ? new Date(expiresAt).toISOString() : undefined,
      adminNotes: adminNotes || undefined,
    });
  };

  const isValid = reason !== '';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Suspend Marketplace Access</DialogTitle>
          <DialogDescription>
            Suspend seller access for @{user?.username}. The user will not be able to create listings, make offers, or access marketplace/exchange features.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Suspension Type */}
          <div className="space-y-2">
            <Label>Suspension Type</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="suspensionType"
                  checked={suspensionType === 'permanent'}
                  onChange={() => setSuspensionType('permanent')}
                  className="w-4 h-4"
                />
                <span>Permanent</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="suspensionType"
                  checked={suspensionType === 'temporary'}
                  onChange={() => setSuspensionType('temporary')}
                  className="w-4 h-4"
                />
                <span>Temporary</span>
              </label>
            </div>
          </div>

          {/* Expiry Date (for temporary) */}
          {suspensionType === 'temporary' && (
            <div className="space-y-2">
              <Label htmlFor="expiresAt">Suspension End Date</Label>
              <Input
                id="expiresAt"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason *</Label>
            <Select value={reason} onValueChange={(value) => setReason(value as SuspensionReason)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {SUSPENSION_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason Details */}
          <div className="space-y-2">
            <Label htmlFor="reasonDetails">Details (shown to user)</Label>
            <Textarea
              id="reasonDetails"
              placeholder="Additional details about the suspension reason..."
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)}
              maxLength={1000}
              rows={3}
            />
            <p className="text-xs text-neutral-500">{reasonDetails.length}/1000 characters</p>
          </div>

          {/* Admin Notes */}
          <div className="space-y-2">
            <Label htmlFor="adminNotes">Admin Notes (internal only)</Label>
            <Textarea
              id="adminNotes"
              placeholder="Internal notes about this suspension..."
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              maxLength={500}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isPending}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {isPending ? 'Suspending...' : 'Suspend Seller'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SuspendSellerModal;
